import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

import { confirmCode, requestSmsCode } from '../../api/authService';
import {
  registrationIndividual,
  registrationLegalEntity,
} from '../../api/registrationApi';
import resetStore from '../resetStore';

const initialState = {
  error: null,
  fetchStatus: 'idle',
  isCodeSent: false,
  isAuthenticated: false,
  id: null,
};

const translateRegistrationError = (errorCode) => {
  switch (errorCode) {
    case 'invalid_credentials':
      return 'Неверный код. Проверьте СМС и попробуйте снова.';
    default:
      return 'Произошла ошибка. Пожалуйста, попробуйте ещё раз.';
  }
};

export const sendRegistrationCode = createAsyncThunk(
  'registration/sendRegistrationCode',
  async ({ phone, captchaToken }, { rejectWithValue }) => {
    try {
      const res = await requestSmsCode(phone, true, captchaToken);
      if (!res.id) {
        return rejectWithValue('Неверный формат ответа от сервера');
      }
      Cookies.set('registration_phone', phone, { expires: 7 });
      return res;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      return rejectWithValue(message || 'Не удалось отправить СМС. Попробуйте позже.');
    }
  }
);

export const verifyRegistrationCode = createAsyncThunk(
  'registration/verifyRegistrationCode',
  async ({ id, code }, { rejectWithValue }) => {
    try {
      const res = await confirmCode(id, code);
      // Раньше здесь также писалась Cookies.set('auth_token', ...) -
      // теперь сервер сам ставит httpOnly-куку через Set-Cookie, фронту
      // это больше не нужно (см. тот же комментарий в authSlice.js).
      // is_registration - отдельная, обычная кука, не связана с этой
      // миграцией, оставляем как было.
      if (res.error) {
        const translatedError = translateRegistrationError(res.error);
        return rejectWithValue(translatedError);
      }
      Cookies.set('is_registration', 'true', { expires: 7 });
      return res;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      return rejectWithValue(
        message || 'Не удалось подтвердить код. Пожалуйста, попробуйте позже.'
      );
    }
  }
);

export const registerIndividual = createAsyncThunk(
  'registration/registerIndividual',
  async ({ fullName, email, phone }, { rejectWithValue }) => {
    try {
      const res = await registrationIndividual(fullName, email, phone);
      return res;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      console.log(message);
      return rejectWithValue(message || 'Ошибка регистрации пользователя');
    }
  }
);

export const registerLegalEntity = createAsyncThunk(
  'registration/registerLegalEntity',
  async (data, { rejectWithValue }) => {
    try {
      const res = await registrationLegalEntity(data);
      return res;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      return rejectWithValue(message || 'Ошибка регистрации юридического лица');
    }
  }
);

const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(sendRegistrationCode.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(sendRegistrationCode.fulfilled, (state, action) => {
        state.fetchStatus = 'succeeded';
        state.isCodeSent = true;
        state.id = action.payload.id;
      })
      .addCase(sendRegistrationCode.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(verifyRegistrationCode.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(verifyRegistrationCode.fulfilled, (state) => {
        state.fetchStatus = 'succeeded';
        state.isAuthenticated = true;
      })
      .addCase(verifyRegistrationCode.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(registerIndividual.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(registerIndividual.fulfilled, (state) => {
        state.fetchStatus = 'succeeded';
      })
      .addCase(registerIndividual.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(registerLegalEntity.pending, (state) => {
        state.fetchStatus = 'loading';
        state.error = null;
      })
      .addCase(registerLegalEntity.fulfilled, (state) => {
        state.fetchStatus = 'succeeded';
      })
      .addCase(registerLegalEntity.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.error = action.payload;
      })
      .addCase(resetStore, () => initialState);
  },
});

export default registrationSlice.reducer;
